import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, X, Users, GitCompare } from 'lucide-react';
import { getSystem, addTrail, updateTrail, deleteTrail, getFriendsOnSystem } from '../api';
import TrailMap from '../components/TrailMap';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';

const DIFFICULTIES = ['white', 'green', 'blue', 'black', 'dh', 'unknown'];
const DIFFICULTY_LABELS = { white: 'Easy', green: 'Beginner', blue: 'Intermediate', black: 'Advanced', dh: 'Expert', unknown: 'Unknown' };

function DifficultyBadge({ difficulty }) {
  return <span className={`badge badge--${difficulty}`}>{DIFFICULTY_LABELS[difficulty] || difficulty}</span>;
}

function AddTrailModal({ systemId, onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', difficulty: 'unknown', length: '', elevationGain: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await addTrail(systemId, {
        ...form,
        length: form.length ? parseFloat(form.length) : undefined,
        elevationGain: form.elevationGain ? parseFloat(form.elevationGain) : undefined,
      });
      onAdded();
      onClose();
    } catch {
      toast.error('Failed to add trail.');
    } finally {
      setSaving(false);
    }
  }

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="modal__overlay" onClick={onClose}>
      <div className="modal__content" onClick={e => e.stopPropagation()}>
        <h2>ADD TRAIL</h2>
        <form onSubmit={handleSubmit}>
          <div className="form__group">
            <label>Trail Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Freight Train" required />
          </div>
          <div className="form__row">
            <div className="form__group">
              <label>Difficulty</label>
              <select value={form.difficulty} onChange={e => set('difficulty', e.target.value)}>
                {DIFFICULTIES.map(d => <option key={d} value={d}>{DIFFICULTY_LABELS[d]}</option>)}
              </select>
            </div>
            <div className="form__group">
              <label>Length (km)</label>
              <input type="number" step="0.1" value={form.length} onChange={e => set('length', e.target.value)} placeholder="e.g. 4.2" />
            </div>
          </div>
          <div className="form__group">
            <label>Elevation Gain (m)</label>
            <input type="number" value={form.elevationGain} onChange={e => set('elevationGain', e.target.value)} placeholder="e.g. 320" />
          </div>
          <div className="form__group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes..." rows={2} />
          </div>
          <div className="form__actions">
            <button type="button" className="btn btn--secondary flex-1" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary flex-1" disabled={saving}>
              {saving ? <span className="spinner" /> : 'ADD TRAIL'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SystemDetail() {
  const { id } = useParams();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'done' | 'todo'
  const [diffFilter, setDiffFilter] = useState('all');
  const [friendsOnSystem, setFriendsOnSystem] = useState([]);
  const toast = useToast();
  const confirm = useConfirm();

  const load = () => {
    getSystem(id).then(res => setSystem(res.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  useEffect(() => {
    getFriendsOnSystem(id)
      .then(res => setFriendsOnSystem(res.data))
      .catch(() => {});
  }, [id]);

  async function toggleComplete(trail) {
    const updates = {
      completed: !trail.completed,
      completedDate: !trail.completed ? new Date().toISOString() : null,
    };
    const total = trails.length;
    const oldDone = trails.filter(t => t.completed).length;
    try {
      await updateTrail(id, trail._id, updates);
      const newDone = updates.completed ? oldDone + 1 : oldDone - 1;
      if (total > 0 && updates.completed) {
        const oldPct = Math.round((oldDone / total) * 100);
        const newPct = Math.round((newDone / total) * 100);
        const milestones = [100, 75, 50, 25];
        for (const m of milestones) {
          if (newPct >= m && oldPct < m) {
            if (m === 100) {
              toast.success(`You've completed every trail in ${system.name}!`);
            } else {
              toast.success(`${m}% of ${system.name} complete — keep going!`);
            }
            break;
          }
        }
      }
      load();
    } catch {
      toast.error('Failed to update trail. Please try again.');
    }
  }

  async function handleDeleteTrail(trailId) {
    const ok = await confirm('Remove this trail?', { confirmLabel: 'Remove' });
    if (!ok) return;
    try {
      await deleteTrail(id, trailId);
      load();
    } catch {
      toast.error('Failed to delete trail. Please try again.');
    }
  }

  if (loading) return <div className="loading"><div className="spinner spinner--lg" /></div>;
  if (!system) return <div>System not found. <Link to="/systems">Back</Link></div>;

  const trails = system.trails || [];
  const done = trails.filter(t => t.completed).length;
  const pct = trails.length > 0 ? Math.round((done / trails.length) * 100) : 0;

  let filtered = trails;
  if (filter === 'done') filtered = filtered.filter(t => t.completed);
  if (filter === 'todo') filtered = filtered.filter(t => !t.completed);
  if (diffFilter !== 'all') filtered = filtered.filter(t => t.difficulty === diffFilter);

  return (
    <div className="fade-in">
      <Link to="/systems" className="back-link">← Back to Systems</Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-header__title" style={{ lineHeight: 1 }}>{system.name}</h1>
          <div className="page-header__subtitle" style={{ marginTop: '4px' }}>
            {[system.region, system.country].filter(Boolean).join(', ')}
          </div>
        </div>
        <button className="btn btn--primary" onClick={() => setShowAddModal(true)}>+ ADD TRAIL</button>
      </div>

      {/* Progress */}
      <div className="card mb-lg">
        <div className="progress-section__header">
          <span className="progress-section__label">PROGRESS</span>
          <span className="progress-section__value">{done}/{trails.length} trails · {pct}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-bar__fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Friends on this system */}
      {friendsOnSystem.length > 0 && (
        <div className="card mb-lg system-friends">
          <div className="system-friends__header">
            <Users size={14} />
            <span className="system-friends__title">FRIENDS ON THIS SYSTEM</span>
          </div>
          <div className="system-friends__list">
            {friendsOnSystem.map(f => (
              <div key={f._id} className="system-friends__item">
                <div className="system-friends__avatar">
                  {(f.username || f.firstName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <span className="system-friends__name">{f.username || f.firstName}</span>
                  <span className="system-friends__pct">{f.pct}%</span>
                </div>
                <div className="progress-bar system-friends__bar">
                  <div className="progress-bar__fill" style={{ width: `${f.pct}%` }} />
                </div>
                <Link to={`/friends/${f._id}/compare/${id}`} className="system-friends__compare">
                  <GitCompare size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map */}
      <TrailMap trails={trails} />

      {/* Filters */}
      <div className="filter-bar">
        {['all', 'todo', 'done'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`btn btn--sm ${filter === f ? 'btn--primary' : 'btn--secondary'}`}
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.05em' }}>
            {f === 'all' ? 'ALL' : f === 'todo' ? 'TO RIDE' : 'RIDDEN'}
          </button>
        ))}
        <select value={diffFilter} onChange={e => setDiffFilter(e.target.value)} className="filter-bar__select">
          <option value="all">All Difficulties</option>
          {DIFFICULTIES.map(d => <option key={d} value={d}>{DIFFICULTY_LABELS[d]}</option>)}
        </select>
      </div>

      {/* Trail List */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <p>No trails match this filter.</p>
        </div>
      ) : (
        <div className="grid grid--gap-sm">
          {filtered.map(trail => (
            <div key={trail._id} className={`card trail-item${trail.completed ? ' trail-item--completed' : ''}`}>
              {/* Checkbox */}
              <button role="checkbox" aria-checked={trail.completed} aria-label={`Mark ${trail.name} as ${trail.completed ? 'incomplete' : 'complete'}`} onClick={() => toggleComplete(trail)}
                className={`trail-item__checkbox${trail.completed ? ' trail-item__checkbox--checked' : ''}`}>
                {trail.completed && <Check size={14} strokeWidth={3} />}
              </button>

              {/* Info */}
              <div className="trail-item__info">
                <div className="trail-item__name-row">
                  <span className={trail.completed ? 'trail-item__name--completed' : ''} style={{ fontWeight: 500 }}>
                    {trail.name}
                  </span>
                  <DifficultyBadge difficulty={trail.difficulty} />
                </div>
                <div className="trail-item__meta">
                  {trail.length && <span>{trail.length} km</span>}
                  {trail.elevationGain && <span>↑ {trail.elevationGain}m</span>}
                  {trail.completedDate && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Check size={11} /> {new Date(trail.completedDate).toLocaleDateString()}</span>}
                  {trail.stravaActivityId && <a href={`https://www.strava.com/activities/${trail.stravaActivityId}`} target="_blank" rel="noreferrer" className="trail-item__strava-link">View on Strava</a>}
                </div>
                {trail.notes && <div className="trail-item__notes">{trail.notes}</div>}
              </div>

              <button className="btn btn--danger btn--sm" aria-label={`Delete ${trail.name}`} onClick={() => handleDeleteTrail(trail._id)}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {showAddModal && <AddTrailModal systemId={id} onClose={() => setShowAddModal(false)} onAdded={load} />}
    </div>
  );
}
